import { useMemo, useEffect, useCallback, useContext, useState } from 'react';
import { FormGroup, Input } from 'src/components/ui';
import Select, { OptionsOrGroups } from 'react-select';
import { useLingui } from '@lingui/react';

import Notification from '../Notification';
import Loader from '../Loader';
import { EmptyState } from 'src/components/Icons/EmptyStates';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import { concatenatePaths } from '../../util/mapObject';
import getPathFromId from '../../util/getPathFromId';
import Jnx, { JnxExpr, useJnx } from '../../util/jnx';
import useOptionsLookup, { LookupObject, getLookupCacheKey } from './hooks/useOptionsLookup';
import CollapsableTreeSelect, { makeTreeNode, organizeTreeNodes, visitTrees } from '../CollapsableTreeSelect';
import { t } from '@lingui/core/macro';
import { FormFieldProps, FormPath } from '../FormComponentTypes';
import { AuthContext } from '../../context/AuthContext';
import useWrapInput from './utils/useWrapInput';
import useTestId from '../../hooks/useTestId';
import ArrayLookupCacheContext, { useArrayLookupCacheContext, useOptionsLookupWithCache } from './ArrayLookupCacheContext';

/** Translates raw option VALUES coming back from the lookup's API — distinct from
 *  the workpage-wide `i18n` convention (which translates schema-authored strings),
 *  since these values live in whichever locale the backend happens to store them in.
 *  Both `sourceLocale` and `options` are required; either missing falls back to the
 *  raw, untranslated value. */
export type LookupOptionsI18n = {
  sourceLocale?: string;
  options?: Record<string, string>;
};

export type LookupFieldObject = LookupObject & {
  resource?: string;
  setObjectAs?: FormPath;
  allowNoValue?: boolean;
  noValue?: string;
  registry: Object;
  parentId?: boolean;
  collapsable?: boolean;
  collapseLevel?: number;
  label: JnxExpr<string>;
  id: JnxExpr<string | number>;
  groupBy?: JnxExpr<string>;
  defaultValueExpr: JnxExpr<number | string>;
  firstSelected?: boolean;
  i18n?: LookupOptionsI18n;
};

export type LookupFormFieldProps = FormFieldProps<
  number | string,
  {
    lookup: LookupFieldObject;
    type: 'number' | 'string';
  },
  {
    'ui:onSelect': JnxExpr<void | Promise<void>>;
    'ui:useTypeahead'?: boolean;
    'ui:classNames'?: string;
    'ui:hideLabel'?: boolean;
    'ui:prefix'?: string;
    'ui:sufix'?: string;
    'ui:staticText'?: string;
    'ui:clearable'?: boolean;
    'ui:placeholder'?: string;
    'ui:displayOptionsAsList'?: boolean;
    'ui:listMaxHeight'?: number | string;
    'ui:emptyStateMessage'?: string;
    'ui:selectableTypes'?: string[];
    'ui:selectableTypesExpr'?: JnxExpr<string[]>;
    'ui:selectableNames'?: string[];
    'ui:selectableNamesExpr'?: JnxExpr<string[]>;
    'ui:infoBadge'?: unknown;
  }
>;

/** The value as a number when it parses as one (option ids are often numeric), otherwise unchanged. */
function convertIfNumber(value: any) {
  const num = Number(value);

  return Number.isNaN(num) ? value : num;
}

function LookupFormField(props: LookupFormFieldProps) {
  const {
    formData,
    registry,
    disabled,
    readonly: propReadonly,
    schema: { title, lookup, type },
    fieldPathId: { $id, path: _path } = {},
    uiSchema: {
      'akc:requiredIfVisible': akcRequiredIfVisible,
      'ui:readonly': uiReadonly,
      'ui:readonlyIf': uiReadonlyIf,
      'ui:onSelect': onSelectExpr,
      'ui:useTypeahead': uiTypeahead,
      'ui:classNames': classNames,
      'ui:hideLabel': hideLabel = false,
      'ui:prefix': prefix,
      'ui:sufix': sufix,
      'ui:staticText': staticText = false,
      'ui:clearable': clearable = false,
      'ui:placeholder': placeholder = '',
      'ui:displayOptionsAsList': displayOptionsAsList = false,
      'ui:listMaxHeight': listMaxHeight,
      'ui:emptyStateMessage': emptyStateMessage = '',
      'ui:selectableTypes': selectableTypes = null,
      'ui:selectableTypesExpr': selectableTypesExpr = null,
      'ui:selectableNames': selectableNames = null,
      'ui:selectableNamesExpr': selectableNamesExpr = null,
      'ui:infoBadge': infoBadge = null,
    },
    required: requiredProp,
    onChange: propOnChange,
  } = props;

  const formContext = registry?.formContext;
  const { sideChannel, setFormDataValues } = formContext || {};

  const readonly = propReadonly || uiReadonly;
  const required = requiredProp || akcRequiredIfVisible;

  const {
    resource = t`Options`,
    setObjectAs,
    allowNoValue = true,
    noValue = '',
    parentId,
    collapsable,
    collapseLevel,
    label: labelExpr,
    id: idExpr,
    groupBy: groupByExpr,
    firstSelected = false,
    i18n: lookupI18n,
  } = lookup || {};

  const { i18n } = useLingui();
  const translateOptionLabel = useCallback(
    (raw: any) => {
      if (!lookupI18n?.sourceLocale || !lookupI18n?.options) return raw;
      if (lookupI18n.sourceLocale === i18n.locale) return raw;
      if (raw == null) return raw;
      const key = String(raw);
      return key in lookupI18n.options ? lookupI18n.options[key] : raw;
    },
    [lookupI18n, i18n.locale]
  );

  const auth = useContext(AuthContext);
  const testIdProps = useTestId($id);

  const dataPath = useMemo(() => getPathFromId($id), [$id]);

  const objectPath = useMemo(() => (setObjectAs ? concatenatePaths(dataPath, `..${setObjectAs}`) : null), [dataPath, setObjectAs]);
  const functionBinds = useMemo(() => ({ set: setFormDataValues }), [setFormDataValues]);
  const onSelectJnx = useJnx(onSelectExpr, { functionBinds });

  const [rootFormData, formObject] = useSideChannelSubscription(sideChannel, [0, 1]) || [{}, {}];

  const { options: lookupOptions, loadingOptions, errorLoadingOptions } = useOptionsLookupWithCache({
    lookup,
    rootFormData,
    path: dataPath,
    auth,
    formContext,
    formObject
  });

  const labelJnx = useJnx(labelExpr);
  const idJnx = useJnx(idExpr);

  const groupByJnx = useJnx(groupByExpr);
  const options = useParsedOptions(lookupOptions, lookup, groupByJnx, labelJnx, idJnx);

  const onChange = ({ target: { value } }: { target: { value: string | number } }) => {
    if (type === 'number') {
      value = +value | 0;
    }

    propOnChange(value, _path);
    // postChange(value);
  };

  const defaultValueJnx = useJnx(lookup.defaultValueExpr);
  const defaultValue = useMemo(
    () =>
      defaultValueJnx &&
      defaultValueJnx.eval(rootFormData || {}, '', {
        root: rootFormData,
        formContext,
      }),
    [rootFormData, formContext, defaultValueJnx]
  );

  const dataValue = formData || defaultValue;

  useEffect(() => {
    if (formData == null && defaultValue != null) {
      onChange({ target: { value: defaultValue } });
    }
  }, [formData, defaultValue]);

  const readonlyIfJnx = useJnx(uiReadonlyIf);
  const readOnlyIf = useMemo(
    () =>
      readonlyIfJnx &&
      readonlyIfJnx.eval(rootFormData || {}, '', {
        root: rootFormData,
        formContext,
      }),
    [dataValue, rootFormData, formContext]
  );

  const postChange = (newVal: any) => {
    if (!flatOptions) {
      return;
    }

    const object = (flatOptions || []).filter((option: any) => {
      const parsedValue = convertIfNumber(newVal);

      return option.id === parsedValue;
    })[0]?.item;

    if (!object) return;

    if (onSelectJnx) {
      setTimeout(() => {
        onSelectJnx.eval(object, '', { fieldPath: dataPath, object });
      }, 500);
    }

    if (!objectPath) {
      return;
    }

    setFormDataValues({ [objectPath]: object });
  };

  const isDisabled = readonly || readOnlyIf || disabled;

  const flatOptions = useMemo(() => {
    if (!options) return null;
    if (Array.isArray(options) && options.length && 'options' in options[0]) {
      return (options as any[]).reduce((acc: any[], grp: any) => {
        if (grp && grp.options) return acc.concat(grp.options);
        return acc;
      }, [] as any[]);
    }
    return options;
  }, [options]);

  const fSelected = firstSelected && !dataValue && idJnx && flatOptions ? idJnx.eval(flatOptions?.[0]) : null;

  const getSelectedOption = useCallback(() => {
    if (!dataValue && !fSelected) return null;
    return (
      flatOptions?.find((o: any) => {
        if (dataValue) {
          return (idJnx ? idJnx.eval(o.item) : o.id) === dataValue;
        } else if (fSelected) {
          return (idJnx ? idJnx.eval(o.item) : o.id) === fSelected;
        }
      }) || null
    );
  }, [dataValue, fSelected, flatOptions, idJnx]);

  const inputNode = useMemo(() => {
    const value = getSelectedOption();
    if (staticText) {
      if (dataValue || fSelected) {
        const label = translateOptionLabel(value?.label ? value.label : labelJnx ? labelJnx.eval(value?.item) : t`(empty)`);
        return (
          <div className={classNames || ''} {...testIdProps}>
            {label}
          </div>
        );
      }
      return <div className={classNames || ''} {...testIdProps}>{t`(empty)`}</div>;
    }

    if (displayOptionsAsList) {
      if (loadingOptions) {
        return (
          <div>
            <Loader dots inline size={30}>
              {t`Loading`} {resource}
            </Loader>
          </div>
        );
      }

      return (
        <LookupOptionsList
          options={options}
          labelJnx={labelJnx}
          translateOptionLabel={translateOptionLabel}
          emptyStateMessage={emptyStateMessage}
          maxHeight={listMaxHeight}
          testIdProps={testIdProps}
        />
      );
    }

    if (uiTypeahead !== false) {
      return (
        <Select
          id={$id}
          inputId={$id}
          {...testIdProps}
          className="autocomplete-container"
          classNamePrefix="autocomplete"
          menuPortalTarget={document.body}
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          options={options as OptionsOrGroups<any, any>}
          onChange={o => {
            if (clearable && o == null) {
              propOnChange(undefined as any, _path);
              postChange(undefined);
              return;
            }
            let id = idJnx ? idJnx.eval(o.item) : o.id;
            if (type === 'number') {
              id = +id | 0;
            }
            propOnChange(id, _path);
            postChange(id);
          }}
          placeholder={placeholder}
          getOptionValue={o => (idJnx ? idJnx.eval(o.item) : o.id)}
          getOptionLabel={o => translateOptionLabel(labelJnx ? labelJnx.eval(o.item) : o.label)}
          value={value}
          isDisabled={isDisabled}
          required={required}
          isClearable={clearable}
        />
      );
    }

    return (
      <Input
        id={$id}
        {...testIdProps}
        type="select"
        className="form-control"
        disabled={isDisabled}
        required={required}
        value={dataValue || fSelected || ''}
        onChange={onChange}
      >
        {allowNoValue ? <option value={undefined}>{noValue}</option> : null}
        {Array.isArray(options) && options.length && 'options' in options[0]
          ? // grouped structure
            (options as any[]).map((group: any, gi: number) => (
              <optgroup key={gi} label={group.label}>
                {group.options.map((option: any, idx: number) => (
                  <option key={idx} value={idJnx ? idJnx.eval(option.item) : option.id}>
                    {translateOptionLabel(labelJnx ? labelJnx.eval(option.item) : option.label)}
                  </option>
                ))}
              </optgroup>
            ))
          : // flat list
            (options || []).map((option, idx) => (
              <option key={idx} value={idJnx ? idJnx.eval(option.item) : option.id}>
                {translateOptionLabel(labelJnx ? labelJnx.eval(option.item) : option.label)}
              </option>
            ))}
      </Input>
    );
  }, [
    uiTypeahead,
    allowNoValue,
    isDisabled,
    required,
    dataValue,
    fSelected,
    onChange,
    options,
    noValue,
    getSelectedOption,
    propOnChange,
    idJnx,
    displayOptionsAsList,
    loadingOptions,
    labelJnx,
    translateOptionLabel,
    emptyStateMessage,
    listMaxHeight,
    testIdProps,
  ]);

  const wrappedInput = useWrapInput({
    input: inputNode,
    prefix,
    sufix,
  });

  const _selectableTypes = useMemo(() => {
    if (selectableTypesExpr) {
      return new Jnx(selectableTypesExpr).eval(rootFormData, '', { formContext });
    }

    if (selectableTypes && selectableTypes.length) return selectableTypes;
    return null;
  }, [selectableTypes, selectableTypesExpr, formContext, rootFormData]);

  const _selectableNames = useMemo(() => {
    if (selectableNamesExpr) {
      return new Jnx(selectableNamesExpr).eval(rootFormData, '', { formContext });
    }

    if (selectableNames && selectableNames.length) return selectableNames;
    return null;
  }, [selectableNames, selectableNamesExpr, formContext, rootFormData]);

  return (
    <FormGroup className={classNames} disabled={readonly || readOnlyIf || disabled}>
      {title !== ' ' && !hideLabel ? (
        <label className="control-label" htmlFor="root_preferredBranchId">
          {title}
          {required ? <span className="required">*</span> : null}
        </label>
      ) : null}
      {loadingOptions ? (
        <div>
          <Loader dots inline size={30}>
            {t`Loading`} {resource}
          </Loader>
        </div>
      ) : errorLoadingOptions ? (
        <Notification color="danger">
          <div>{errorLoadingOptions.message}</div>
        </Notification>
      ) : parentId && collapsable ? (
        <CollapsableTreeSelect
          disabled={isDisabled}
          defaultCollapseLevel={collapseLevel}
          required={required}
          selectableTypes={_selectableTypes}
          selectableNames={_selectableNames}
          value={dataValue}
          rootNodes={options}
          placeholder={placeholder}
          onChange={onChange}
          infoBadge={infoBadge}
        />
      ) : (
        wrappedInput
      )}
    </FormGroup>
  );
}

// Above this many options, showing a search box earns back more than it costs in clutter.
const LIST_SEARCH_THRESHOLD = 8;

type OptionEntry = { item: any; label?: string };
type OptionGroup = { label: string | null; options: OptionEntry[] };

type LookupOptionsListProps = {
  options: any[] | null | undefined;
  labelJnx: Jnx | null;
  translateOptionLabel: (raw: any) => any;
  emptyStateMessage?: string;
  maxHeight?: number | string;
  testIdProps: Record<string, unknown>;
};

/** Read-only, searchable rendering of `ui:displayOptionsAsList` — a scrollable list (optionally
 *  grouped) with a filter box that appears once there are enough options to warrant one.
 */
function LookupOptionsList({ options, labelJnx, translateOptionLabel, emptyStateMessage, maxHeight, testIdProps }: LookupOptionsListProps) {
  const [search, setSearch] = useState('');

  const isGrouped = Array.isArray(options) && options.length > 0 && 'options' in options[0];
  const getLabel = useCallback((o: OptionEntry) => `${translateOptionLabel(labelJnx ? labelJnx.eval(o.item) : o.label ?? '')}`, [
    labelJnx,
    translateOptionLabel,
  ]);

  const totalCount = useMemo(() => {
    if (!options) return 0;
    return isGrouped ? (options as { options: OptionEntry[] }[]).reduce((sum, g) => sum + (g.options?.length || 0), 0) : options.length;
  }, [options, isGrouped]);

  const groups: OptionGroup[] = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = (o: OptionEntry) =>
      !query ||
      getLabel(o)
        .toLowerCase()
        .includes(query);

    if (isGrouped) {
      return (options as { label: string; options: OptionEntry[] }[])
        .map(group => ({ label: group.label, options: (group.options || []).filter(matches) }))
        .filter(group => group.options.length > 0);
    }

    const flat = (options || []).filter(matches);
    return flat.length ? [{ label: null, options: flat }] : [];
  }, [options, isGrouped, search, getLabel]);

  if (totalCount === 0) {
    return <EmptyState icon="inbox" size={96} message={emptyStateMessage || t`No options to show`} />;
  }

  const hasResults = groups.length > 0;
  const visibleCount = groups.reduce((sum, g) => sum + g.options.length, 0);
  const countLabel = search.trim() ? t`${visibleCount} of ${totalCount}` : totalCount === 1 ? t`1 option` : t`${totalCount} options`;

  return (
    <div className="lookup-list-field">
      <div className="lookup-list-toolbar">
        {totalCount > LIST_SEARCH_THRESHOLD && (
          <Input bsSize="sm" placeholder={t`Search…`} value={search} onChange={e => setSearch(e.target.value)} aria-label={t`Search options`} />
        )}
        <span className="lookup-list-count text-muted">{countLabel}</span>
      </div>
      <div className="lookup-list-items" style={maxHeight ? { maxHeight } : undefined} {...testIdProps}>
        {!hasResults ? (
          <EmptyState icon="search" size={96} message={t`No matches found`} filtersApplied clearFilters={() => setSearch('')} />
        ) : (
          groups.map((group, gi) => (
            <div key={gi}>
              {group.label != null && <div className="lookup-list-group-label">{group.label}</div>}
              {group.options.map((o, i) => (
                <div key={i} className="lookup-list-item">
                  {getLabel(o)}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function useParsedOptions(
  options: any[] | null | undefined,
  { parentId, id, label, groupBy }: LookupFieldObject,
  groupByJnx?: any,
  labelJnx?: Jnx | null,
  idJnx?: Jnx | null
) {
  const tsOptions = useMemo(() => {
    if (!options) return null;
    const tsOptions = options.map(item => makeTreeNode(item, id, label, parentId));

    // hierarchical mode takes precedence, grouping doesn't make sense when
    // there is a parentId field driving tree structure.
    if (parentId) {
      const rootNodes = organizeTreeNodes(tsOptions);
      visitTrees(rootNodes);
      tsOptions.sort((a, b) => a.visitIdx - b.visitIdx);

      return rootNodes;
    }

    if (groupByJnx) {
      const groupsMap: Map<string, any[]> = new Map();
      tsOptions.forEach(opt => {
        const key = groupByJnx.eval(opt.item);
        const label = labelJnx ? labelJnx.eval(opt.item) : opt.label;
        const id = idJnx ? idJnx.eval(opt.item) : opt.id;
        const labelKey = key != null ? String(key) : '';
        if (!groupsMap.has(labelKey)) {
          groupsMap.set(labelKey, []);
        }
        groupsMap.get(labelKey)!.push({ ...opt, label, id });
      });
      const groups: any[] = [];
      groupsMap.forEach((opts, lbl) => groups.push({ label: lbl, options: opts }));
      return groups;
    }

    return tsOptions;
  }, [options, parentId, groupByJnx]);

  return tsOptions;
}

export default LookupFormField;
